import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  api,
  type import_preview,
  type import_run,
  type payment,
  type user,
} from '../api'
import {
  format_amount,
  format_bytes,
  format_date,
  format_timestamp,
  is_outgoing,
  status_label,
} from '../format'

export function UserDetailPage() {
  const params = useParams()
  const user_id = Number(params.user_id)
  const navigate = useNavigate()

  const [user, set_user] = useState<user | null>(null)
  const [payments, set_payments] = useState<payment[]>([])
  const [imports, set_imports] = useState<import_run[]>([])
  const [error, set_error] = useState<string | null>(null)
  const [info, set_info] = useState<string | null>(null)
  // Der Bankexport bringt weit mehr Zeilen mit, als je belegpflichtig werden —
  // beim Prüfen stören sie, beim Anfordern werden sie gebraucht.
  const [only_required, set_only_required] = useState(false)

  async function load() {
    const [loaded_user, loaded_payments, loaded_imports] = await Promise.all([
      api.get_user(user_id),
      api.list_payments(user_id),
      api.list_imports(user_id),
    ])
    set_user(loaded_user)
    set_payments(loaded_payments)
    set_imports(loaded_imports)
  }

  useEffect(() => {
    void load()
  }, [user_id])

  async function guarded(action: () => Promise<unknown>, success?: string) {
    set_error(null)
    set_info(null)
    try {
      await action()
      if (success) set_info(success)
      await load()
    } catch (caught) {
      set_error(caught instanceof Error ? caught.message : String(caught))
    }
  }

  if (!user) return <div className="page muted">Wird geladen …</div>

  const visible_payments = only_required
    ? payments.filter((item) => item.receipt_required)
    : payments

  return (
    <div className="page">
      <h1>{user.name}</h1>
      <p className="muted">{user.email}</p>

      {error && <div className="notice notice--error">{error}</div>}
      {info && <div className="notice notice--ok">{info}</div>}

      <UserSettings
        user={user}
        on_save={(changes) => guarded(() => api.update_user(user_id, changes), 'Gespeichert.')}
        on_remind={() =>
          guarded(() => api.send_reminder(user_id), 'Erinnerung mit neuem Link versendet.')
        }
        on_delete={async () => {
          await guarded(() => api.delete_user(user_id))
          navigate('/admin/benutzer')
        }}
      />

      <ImportCard
        user_id={user_id}
        imports={imports}
        on_done={(message) => guarded(async () => undefined, message)}
        on_change={load}
        on_error={set_error}
      />

      <div className="card">
        <div className="row" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>
            Zahlungen ({visible_payments.length}
            {only_required ? ` von ${payments.length}` : ''})
          </h2>
          <span style={{ flex: 1 }} />
          <label className="row small" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={only_required}
              onChange={(event) => set_only_required(event.target.checked)}
              style={{ width: 'auto' }}
            />
            Nur belegpflichtige Zahlungen
          </label>
        </div>
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Gegenpartei</th>
              <th>Verwendungszweck</th>
              <th className="amount">Betrag</th>
              <th>Belegpflicht</th>
              <th>Status</th>
              <th>Belege</th>
            </tr>
          </thead>
          <tbody>
            {visible_payments.map((item) => (
              <tr key={item.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{format_date(item.booking_date)}</td>
                <td>
                  {item.counterparty_name}
                  {item.iban && <div className="small muted">{item.iban}</div>}
                </td>
                <td>{item.purpose}</td>
                <td className={`amount ${is_outgoing(item.amount) ? 'outgoing' : 'incoming'}`}>
                  {format_amount(item.amount)}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={item.receipt_required}
                    disabled={item.review_status === 'accepted'}
                    title={
                      item.review_status === 'accepted'
                        ? 'Angenommen — erst die Annahme zurücknehmen'
                        : 'Beleg anfordern'
                    }
                    onChange={(event) =>
                      guarded(() => api.set_receipt_required(item.id, event.target.checked))
                    }
                    style={{ width: 'auto' }}
                  />
                </td>
                <td>
                  {item.receipt_required ? (
                    <span className={`status status--${item.review_status}`}>
                      {status_label[item.review_status]}
                      {item.receipts.length > 0 && ` (${item.receipts.length})`}
                    </span>
                  ) : (
                    <span className="status status--none">—</span>
                  )}
                </td>
                <td>
                  {item.receipts.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    item.receipts.map((receipt) => (
                      <div key={receipt.id} className="small">
                        <a
                          href={api.receipt_download_url(receipt.id)}
                          title={`${receipt.file_name} speichern`}
                        >
                          {receipt.file_name}
                        </a>{' '}
                        <span className="muted">{format_bytes(receipt.byte_size)}</span>
                      </div>
                    ))
                  )}
                </td>
              </tr>
            ))}
            {visible_payments.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  {payments.length === 0
                    ? 'Noch keine Zahlungen importiert.'
                    : 'Keine belegpflichtigen Zahlungen.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function UserSettings(props: {
  user: user
  on_save: (changes: Partial<user>) => void
  on_remind: () => void
  on_delete: () => void
}) {
  const [interval_days, set_interval_days] = useState(props.user.reminder_interval_days)
  const [paused, set_paused] = useState(props.user.reminders_paused)
  const [confirmation, set_confirmation] = useState('')

  useEffect(() => {
    set_interval_days(props.user.reminder_interval_days)
    set_paused(props.user.reminders_paused)
  }, [props.user])

  return (
    <div className="card">
      <h2>Erinnerungen</h2>
      <div className="row">
        <div className="field" style={{ width: 160 }}>
          <label htmlFor="interval">Intervall (Tage)</label>
          <input
            id="interval"
            type="number"
            min={1}
            max={365}
            value={interval_days}
            onChange={(event) => set_interval_days(Number(event.target.value))}
          />
        </div>
        <label className="row small" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={paused}
            onChange={(event) => set_paused(event.target.checked)}
            style={{ width: 'auto' }}
          />
          Erinnerungen ausgesetzt
        </label>
        <button
          onClick={() =>
            props.on_save({ reminder_interval_days: interval_days, reminders_paused: paused })
          }
        >
          Speichern
        </button>
        <button className="primary" onClick={props.on_remind}>
          Jetzt erinnern
        </button>
      </div>
      <p className="small muted">
        Das Intervall bestimmt auch, wie lange der Anmeldelink gültig ist — mit jeder Erinnerung
        kommt ein frischer, der vorherige wird sofort entwertet. Ausgesetzt heißt: keine
        Erinnerungen, aber ein bereits versendeter Link bleibt bis zu seinem Ablauf nutzbar.
      </p>

      <details>
        <summary className="small muted">Benutzer löschen</summary>
        <p className="small">
          Löscht Zahlungen, Belege und das Protokoll dieses Benutzers unwiderruflich mit. Zum
          Bestätigen den Namen <strong>{props.user.name}</strong> eintippen.
        </p>
        <div className="row">
          <input
            type="text"
            style={{ maxWidth: 280 }}
            value={confirmation}
            onChange={(event) => set_confirmation(event.target.value)}
          />
          <button
            className="danger"
            disabled={confirmation !== props.user.name}
            onClick={props.on_delete}
          >
            Endgültig löschen
          </button>
        </div>
      </details>
    </div>
  )
}

/**
 * Import in zwei Schritten: erst Vorschau, dann Übernahme. Die Datei wird für
 * beides aus dem Browser geschickt, damit der Server zwischen den Schritten
 * keinen Zustand halten muss.
 */
function ImportCard(props: {
  user_id: number
  imports: import_run[]
  on_done: (message: string) => void
  on_change: () => Promise<void>
  on_error: (message: string) => void
}) {
  const file_input = useRef<HTMLInputElement>(null)
  const [file, set_file] = useState<File | null>(null)
  const [preview, set_preview] = useState<import_preview | null>(null)
  const [busy, set_busy] = useState(false)

  async function choose(selected: File) {
    set_file(selected)
    set_preview(null)
    set_busy(true)
    try {
      set_preview(await api.preview_import(props.user_id, selected))
    } catch (caught) {
      props.on_error(caught instanceof Error ? caught.message : String(caught))
      set_file(null)
    } finally {
      set_busy(false)
    }
  }

  async function commit() {
    if (!file) return
    set_busy(true)
    try {
      const result = await api.commit_import(props.user_id, file)
      props.on_done(
        `${result.inserted_rows} Zahlungen übernommen, ${result.skipped_duplicates} als bereits vorhanden übersprungen.`,
      )
      set_file(null)
      set_preview(null)
      await props.on_change()
    } catch (caught) {
      props.on_error(caught instanceof Error ? caught.message : String(caught))
    } finally {
      set_busy(false)
    }
  }

  return (
    <div className="card">
      <h2>Zahlungen importieren</h2>
      <div className="row">
        <input
          ref={file_input}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(event) => {
            const selected = event.target.files?.[0]
            if (selected) void choose(selected)
            event.target.value = ''
          }}
        />
        <button disabled={busy} onClick={() => file_input.current?.click()}>
          CSV auswählen
        </button>
        {file && <span className="small muted">{file.name}</span>}
      </div>

      {preview && (
        <>
          <div className="notice notice--info" style={{ marginTop: 12 }}>
            <strong>{preview.new_rows} neu</strong>, {preview.skipped_duplicates} bereits vorhanden
            (von {preview.total_rows} Zeilen).
          </div>
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Gegenpartei</th>
                <th>Verwendungszweck</th>
                <th className="amount">Betrag</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.entries.map((entry, index) => (
                <tr key={index} style={entry.is_duplicate ? { opacity: 0.5 } : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>{format_date(entry.booking_date)}</td>
                  <td>{entry.counterparty_name}</td>
                  <td>{entry.purpose}</td>
                  <td className={`amount ${is_outgoing(entry.amount) ? 'outgoing' : 'incoming'}`}>
                    {format_amount(entry.amount)}
                  </td>
                  <td className="small">
                    {entry.is_duplicate ? 'bereits vorhanden' : 'wird übernommen'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row" style={{ marginTop: 12 }}>
            <button className="primary" disabled={busy || preview.new_rows === 0} onClick={commit}>
              {preview.new_rows} Zahlungen übernehmen
            </button>
            <button
              disabled={busy}
              onClick={() => {
                set_file(null)
                set_preview(null)
              }}
            >
              Abbrechen
            </button>
          </div>
        </>
      )}

      {props.imports.length > 0 && (
        <table style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Import</th>
              <th>Datei</th>
              <th>Übernommen</th>
              <th>Übersprungen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.imports.map((run) => (
              <tr key={run.id}>
                <td className="small muted">{format_timestamp(run.created_at)}</td>
                <td>{run.file_name}</td>
                <td>{run.inserted_rows}</td>
                <td>{run.skipped_duplicates}</td>
                <td>
                  <button
                    className="danger"
                    disabled={run.has_receipts}
                    title={
                      run.has_receipts
                        ? 'An diesem Import hängen bereits Belege — er ist endgültig.'
                        : 'Import zurücknehmen'
                    }
                    onClick={async () => {
                      try {
                        await api.revert_import(props.user_id, run.id)
                        props.on_done('Import zurückgenommen.')
                        await props.on_change()
                      } catch (caught) {
                        props.on_error(caught instanceof Error ? caught.message : String(caught))
                      }
                    }}
                  >
                    Zurücknehmen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
